import { request } from '@/shared/api/http'
import type {
  AutoWithdrawalCreate, AutoWithdrawalListResponse, AutoWithdrawalResponse,
  AutoWithdrawalUpdate, Currency, DepositAddressResponse, InvoiceCreate,
  InvoicePageResponse,
  InvoiceResponse, MessageResponse, TransactionDetailResponse,
  TransactionPageResponse, TxStatus, TxType, WalletCreate, WalletListResponse,
  WalletResponse, WalletUpdate, WithdrawalPageResponse, WithdrawalResponse,
} from '@/shared/api/types'

export interface HistoryFilter {
  [key: string]: string | number | undefined
  limit?: number
  offset?: number
  currency?: Currency
  type?: TxType
  status?: TxStatus
  date_from?: string
  date_to?: string
  search?: string
}

/** Деньги мерчанта: приём, история, кошельки, вывод, автовыводы. */
export const money = {
  // ---- приём ----
  /**
   * Адрес пополнения аккаунта — то, что показывает экран «Получить».
   *
   * POST, а не GET, потому что вызов МЕНЯЕТ состояние: если адреса ещё
   * нет или прошлый уже отработал, он выдаёт новый и закрепляет его
   * за аккаунтом. Ставить на такое GET значит однажды получить лишний
   * адрес от префетча браузера или повтора прокси.
   */
  depositAddress(currency: Currency) {
    return request<DepositAddressResponse>(
      `/deposits/address/${encodeURIComponent(currency)}`, { method: 'POST' },
    )
  },
  createInvoice(body: InvoiceCreate, idempotencyKey: string) {
    // Ключ идемпотентности обязателен: без него повтор запроса
    // при обрыве связи создаёт вторую заявку на ту же покупку.
    return request<InvoiceResponse>('/invoices', {
      method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey },
    })
  },
  invoices(query: { limit?: number; offset?: number; status?: string } = {}) {
    return request<InvoicePageResponse>('/invoices', { query })
  },

  // ---- история ----
  transactions(filter: HistoryFilter = {}) {
    return request<TransactionPageResponse>('/transactions', { query: filter })
  },
  transaction(id: string) {
    return request<TransactionDetailResponse>(`/transactions/${encodeURIComponent(id)}`)
  },

  // ---- кошельки ----
  wallets() {
    return request<WalletListResponse>('/wallets')
  },
  addWallet(body: WalletCreate) {
    return request<WalletResponse>('/wallets', { method: 'POST', body })
  },
  updateWallet(id: string, body: WalletUpdate) {
    return request<WalletResponse>(`/wallets/${encodeURIComponent(id)}`, {
      method: 'PATCH', body,
    })
  },
  deleteWallet(id: string) {
    return request<MessageResponse>(`/wallets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },

  // ---- вывод ----
  withdrawals(query: { limit?: number; offset?: number; status?: string } = {}) {
    return request<WithdrawalPageResponse>('/withdrawals', { query })
  },
  /**
   * Комиссия сети, которую спишут СВЕРХ суммы. Без адреса — оценка для
   * получателя без этой монеты: дороже, зато «весь баланс минус комиссия»
   * по ней не упрётся в нехватку, какой бы адрес потом ни ввели.
   */
  withdrawalFee(currency: Currency, address?: string) {
    return request<{ currency: Currency; fee: string }>('/withdrawals/fee', {
      query: address ? { currency, address } : { currency },
    })
  },
  createWithdrawal(
    currency: Currency, address: string, amount: string, idempotencyKey: string,
    fee?: string,
  ) {
    // Адрес — свободный, а не кошелёк из списка: так решил заказчик,
    // так в макете. Сумма строкой: пересчёт в number на пути к серверу —
    // это потерянные разряды и уехавшая сумма перевода. Комиссия — та,
    // что мерчант видел: выросла к моменту заявки — сервер откажет,
    // а не спишет больше показанного.
    return request<WithdrawalResponse>('/withdrawals', {
      method: 'POST',
      body: fee === undefined ? { currency, address, amount } : { currency, address, amount, fee },
      headers: { 'Idempotency-Key': idempotencyKey },
    })
  },
  cancelWithdrawal(id: string) {
    return request<WithdrawalResponse>(
      `/withdrawals/${encodeURIComponent(id)}/cancel`, { method: 'POST' },
    )
  },

  // ---- автовыводы ----
  autoWithdrawals() {
    return request<AutoWithdrawalListResponse>('/auto-withdrawals')
  },
  addAutoWithdrawal(body: AutoWithdrawalCreate) {
    return request<AutoWithdrawalResponse>('/auto-withdrawals', { method: 'POST', body })
  },
  updateAutoWithdrawal(id: string, body: AutoWithdrawalUpdate) {
    return request<AutoWithdrawalResponse>(
      `/auto-withdrawals/${encodeURIComponent(id)}`, { method: 'PATCH', body },
    )
  },
  deleteAutoWithdrawal(id: string) {
    return request<MessageResponse>(
      `/auto-withdrawals/${encodeURIComponent(id)}`, { method: 'DELETE' },
    )
  },
}
